import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Shield, AlertTriangle, Stethoscope, DollarSign, Clock, Scale, Users, ArrowRight, ExternalLink, XCircle } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const WhenToHireLawyerPage = () => {
  const faqData = [
    {
      question: "Do I need a lawyer for a minor car accident?",
      answer: "Maybe not. If your injuries are truly minor (no ongoing symptoms after a week or two), liability is clear, and you're confident dealing with insurance, you may not need an attorney. But if you're unsure, a free consultation costs you nothing."
    },
    {
      question: "How much does a car accident lawyer cost?",
      answer: "Most personal injury lawyers work on contingency—you pay nothing upfront, and the fee (typically 33% of the settlement) comes from your recovery. If you don't win, you don't pay. This means cost shouldn't prevent you from at least consulting with an attorney."
    },
    {
      question: "Will hiring a lawyer make the insurance company take me more seriously?",
      answer: "Yes. Insurance Research Council studies show represented claimants receive 3.5 times higher settlements on average. Insurance companies know attorneys understand claim values and are willing to go to trial if needed."
    },
    {
      question: "Can I hire a lawyer after I've already talked to the insurance company?",
      answer: "Yes, but the sooner the better. If you've given a recorded statement or signed documents, bring those to the consultation. An attorney can still help, though early involvement is ideal."
    },
    {
      question: "What if I can't afford medical treatment right now?",
      answer: "A lawyer can help with this. We can often arrange for medical treatment on a lien basis (the provider waits for payment until settlement) or guide you through using your PIP or health insurance coverage."
    }
  ];

  const signs = [
    {
      title: "You Have Significant Injuries",
      description: "Broken bones, herniated discs, head injuries, or anything requiring surgery or ongoing treatment. These cases have higher value and more complexity.",
      icon: Stethoscope,
      urgency: "high"
    },
    {
      title: "Medical Bills Are Piling Up",
      description: "If your medical expenses exceed a few thousand dollars, you likely need help ensuring the insurance company pays fairly for all treatment—past and future.",
      icon: DollarSign,
      urgency: "high"
    },
    {
      title: "You're Missing Work",
      description: "Lost wages add up quickly. An attorney ensures you're compensated not just for missed days, but for any long-term impact on your earning capacity.",
      icon: Clock,
      urgency: "high"
    },
    {
      title: "The Insurance Company Is Pressuring You",
      description: "Calls asking for recorded statements, pressure to accept quick settlements, requests to sign medical releases—these are signs you need protection.",
      icon: AlertTriangle,
      urgency: "high"
    },
    {
      title: "Liability Is Disputed",
      description: "If the insurance company claims you were partially at fault, or if fault is unclear, you need someone who knows how to protect your interests.",
      icon: Scale,
      urgency: "medium"
    },
    {
      title: "Multiple Parties Are Involved",
      description: "Multi-car accidents, accidents involving commercial vehicles, or situations with multiple insurance companies add complexity that benefits from legal guidance.",
      icon: Users,
      urgency: "medium"
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="when-to-hire-lawyer-page">
      <SEOHead 
        title="When Should I Hire a Car Accident Lawyer? | Amy C. Brown Law"
        description="Not sure if you need a lawyer after your car accident? Learn the signs that indicate you should get legal help. Free consultation: (425) 675-1224."
        faqData={faqData}
        serviceData={{
          name: "Car Accident Legal Consultation",
          description: "Free consultation to help you determine if you need legal representation after a car accident in Washington State.",
          category: "Personal Injury Law - Auto Accidents"
        }}
      />

      {/* Hero Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Scale className="w-4 h-4" />
                Know Your Options
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary mb-6 leading-tight">
                When Should I Hire a Car Accident Lawyer?
              </h1>
              
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                You've been in an accident. You're wondering if you really need a lawyer, or if you can handle it yourself. That's a fair question—let me help you answer it.
              </p>

              <div className="bg-accent-soft rounded-xl p-6 mb-8 border-l-4 border-primary">
                <p className="text-slate-700 leading-relaxed">
                  I'll be honest: not every accident requires an attorney. But there are clear signs that you'd benefit from legal help. And since most consultations are free, there's no harm in asking.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a 
                  href="tel:425-675-1224"
                  className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-5 h-5" />
                  Free Consultation
                </a>
                <a 
                  href="sms:425-675-1224"
                  className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold text-lg hover:bg-secondary hover:text-white transition-all"
                  data-testid="hero-text-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  Text Me
                </a>
              </div>

              <div className="flex flex-wrap gap-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Free consultation
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  No fees unless you win
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Honest advice
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-100">
              <h3 className="font-heading text-xl font-bold text-secondary mb-6">Quick Self-Assessment:</h3>
              <p className="text-slate-600 mb-4">You likely need an attorney if you answer "yes" to any of these:</p>
              <div className="space-y-3">
                {[
                  "Did you go to the ER or hospital?",
                  "Are you still in pain after a week?",
                  "Did you miss work because of the accident?",
                  "Is the insurance company pressuring you?",
                  "Are medical bills over $2,000?",
                  "Is the other driver's insurance denying fault?"
                ].map((question, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-5 h-5 border-2 border-slate-300 rounded flex-shrink-0" />
                    <p className="text-slate-700 text-sm">{question}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Signs You Need a Lawyer */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Signs You Should Hire a Car Accident Lawyer
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              These situations indicate your case is complex enough to benefit from legal representation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {signs.map((sign, index) => (
              <div key={index} className={`rounded-xl p-6 border ${sign.urgency === 'high' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full ${sign.urgency === 'high' ? 'bg-red-100' : 'bg-amber-100'}`}>
                    <sign.icon className={`w-6 h-6 ${sign.urgency === 'high' ? 'text-red-600' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-2">{sign.title}</h3>
                    <p className="text-slate-600 text-sm">{sign.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* When You Might Not Need */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-secondary mb-8 text-center">
            When You Might Not Need a Lawyer
          </h2>
          
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <p className="text-slate-700 leading-relaxed mb-6">
              I believe in giving honest advice, even when it doesn't benefit me. Here are situations where you might be okay handling things yourself:
            </p>
            
            <div className="space-y-4">
              {[
                "Your injuries are truly minor (soreness that resolves within a week or two)",
                "Medical treatment was minimal (maybe one doctor visit)",
                "Liability is crystal clear and the other driver's insurance accepts fault",
                "You're comfortable negotiating with the insurance company",
                "The settlement offer fairly covers your actual expenses"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-700">{item}</p>
                </div>
              ))}
            </div>
            
            <div className="mt-6 p-4 bg-amber-50 rounded-lg border-l-4 border-amber-400">
              <p className="text-amber-800">
                <strong>Important:</strong> If you're unsure, err on the side of getting a free consultation. Many injuries don't show their full impact for days or weeks. It costs nothing to ask.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Research Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-3xl font-bold text-secondary mb-8 text-center">
            What Research Shows About Hiring a Lawyer
          </h2>
          
          <div className="bg-primary/5 rounded-2xl p-8 border border-primary/20">
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              You might wonder: "Will I actually come out ahead after paying attorney fees?"
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              The Insurance Research Council—an organization funded by insurance companies—has studied this extensively. Their findings are clear:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-heading text-3xl font-bold text-primary mb-2">3.5x</h3>
                <p className="text-slate-600">Average settlement multiplier for represented claimants vs. unrepresented</p>
                <p className="text-xs text-slate-400 mt-2">Source: Insurance Research Council</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-heading text-3xl font-bold text-primary mb-2">85%</h3>
                <p className="text-slate-600">Of bodily injury insurance payouts go to claimants with attorneys</p>
                <p className="text-xs text-slate-400 mt-2">Source: Insurance Research Council</p>
              </div>
            </div>
            
            <p className="text-slate-700 leading-relaxed">
              <strong>The math:</strong> Even with typical attorney fees of 33%, a 3.5x higher settlement leaves you with significantly more than handling it yourself. That's why insurance companies prefer dealing with unrepresented claimants.
            </p>
          </div>
        </div>
      </section>

      {/* Why I'm Different */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-primary">
            <h2 className="font-heading text-2xl font-bold text-secondary mb-4">
              Why I Give Honest Advice
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Here's something many lawyers won't tell you: I don't take every case. If your case is truly minor and you don't need an attorney, I'll tell you so. I'd rather give you good advice and have you remember me if something more serious happens later.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              I started my career defending insurance companies. I know how they evaluate claims, what makes them pay more, and what makes them dig in. If I think I can significantly improve your outcome, I'll tell you. If I can't, I'll tell you that too.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Call or text me. I'll give you an honest assessment of whether you need help—no pressure, no obligation.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Common Questions About Hiring a Lawyer
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl">
                <h3 className="font-heading font-semibold text-secondary mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Concepts - Entity SEO */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Understanding Legal Representation
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="https://en.wikipedia.org/wiki/Contingent_fee" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Contingency Fee <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Personal_injury_lawyer" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Personal Injury Lawyer <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Attorney%E2%80%93client_privilege" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Attorney-Client Privilege <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Tort" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Tort Law <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
          <p className="text-xs text-slate-400 text-center mt-4">
            Learn more about legal representation on Wikipedia
          </p>
        </div>
      </section>

      {/* Related Car Accident Resources */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Related Car Accident Resources
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/car-crashes" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <Scale className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Car Accident Claims</p>
                <p className="text-sm text-slate-500">Overview of the car accident claim process</p>
              </div>
            </Link>
            <Link to="/what-to-do-after-car-accident" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <CheckCircle className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">What To Do After an Accident</p>
                <p className="text-sm text-slate-500">Step-by-step guide for Washington</p>
              </div>
            </Link>
            <Link to="/how-much-is-my-case-worth" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <DollarSign className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Case Value Factors</p>
                <p className="text-sm text-slate-500">What determines your claim's worth</p>
              </div>
            </Link>
            <Link to="/everett-car-accident-lawyer" className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200 hover:border-primary/30 hover:shadow-md transition-all group">
              <ArrowRight className="w-6 h-6 text-primary" />
              <div>
                <p className="font-medium text-secondary group-hover:text-primary transition-colors">Everett Car Accident Lawyer</p>
                <p className="text-sm text-slate-500">Local representation in Snohomish County</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            Not Sure If You Need a Lawyer?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Let's talk. I'll give you honest advice about your situation—no charge, no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhenToHireLawyerPage;
